import {
  connectSubjectKeys,
  connectSubjects,
  isActiveSubject,
  SubjectKey,
} from "@nimble/datamodel/enums/connectSubjects";
import * as React from "react";

import MultiSelect from "@/components/shared/multi-select";
import { MultiSelectProps } from "@/components/shared/multi-select/multi-select";
import type {
  GroupConfig,
  MultiSelectOption,
} from "@/components/shared/multi-select/multi-select.view";

interface SubjectSelectProps<T = string>
  extends Omit<MultiSelectProps<T>, "options" | "title"> {
  title?: string;
  itemLabel?: string;
  options: MultiSelectOption<T>[];
}

function SubjectSelect({
  value,
  onChange,
  title,
  options,
  ...props
}: SubjectSelectProps) {
  return (
    <MultiSelect
      value={value}
      onChange={onChange}
      {...props}
      options={options}
      title={title || "Subject Areas"}
    />
  );
}

function isVisible(key: SubjectKey, selected: readonly string[] = []): boolean {
  return isActiveSubject(key) || selected.includes(key);
}

export function AllSubjectSelect({
  value,
  ...props
}: Omit<SubjectSelectProps, "options">) {
  const allSubjectOptions = React.useMemo(
    () =>
      connectSubjectKeys
        .filter((key) => isVisible(key, value))
        .map((key) => ({ label: connectSubjects[key].label, value: key })),
    [value],
  );

  return <SubjectSelect value={value} options={allSubjectOptions} {...props} />;
}

const SUBJECT_GROUPS: Record<string, GroupConfig> = {
  available: { label: "Available" },
  other: { label: "Other Subjects" },
};

interface PreferenceSubjectSelectProps
  extends Omit<SubjectSelectProps, "options"> {
  allowedSubjects?: SubjectKey[] | null;
}

export function PreferenceSubjectSelect({
  allowedSubjects,
  value,
  ...props
}: PreferenceSubjectSelectProps) {
  const allowedSet = React.useMemo(
    () => (allowedSubjects ? new Set(allowedSubjects) : null),
    [allowedSubjects],
  );

  const { options, groups } = React.useMemo(() => {
    const entries = Object.entries(connectSubjects).filter(
      ([key, subject]) =>
        subject.label !== connectSubjects.other.label &&
        isVisible(key as SubjectKey, value),
    );

    if (!allowedSet) {
      return {
        options: entries.map(([value, subject]) => ({
          label: subject.label,
          value,
        })),
        groups: undefined,
      };
    }

    const available: MultiSelectOption<string>[] = entries
      .filter(([value]) => allowedSet.has(value as SubjectKey))
      .map(([value, subject]) => ({
        label: subject.label,
        value,
        group: "available",
      }));

    const other: MultiSelectOption<string>[] = entries
      .filter(([value]) => !allowedSet.has(value as SubjectKey))
      .map(([value, subject]) => ({
        label: subject.label,
        value,
        group: "other",
        disabled: true,
      }));

    return {
      options: [...available, ...other],
      groups: SUBJECT_GROUPS,
    };
  }, [allowedSet, value]);

  return (
    <SubjectSelect value={value} options={options} groups={groups} {...props} />
  );
}
